import React, { ReactNode, useState } from "react";

interface Tab {
  label: string;
  component: ReactNode;
}

interface TabSwitcherWebProps {
  tabs: Tab[];
  onTabChange?: (index: number) => void;
  full?: boolean;
  compact?: boolean;
}

const TabSwitcherWeb: React.FC<TabSwitcherWebProps> = ({
  tabs,
  onTabChange,
  full = false,
  compact = true,
}) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  const handleTabSelect = (index: number) => {
    if (index !== activeTabIndex) {
      setActiveTabIndex(index);
      onTabChange?.(index);
    }
  };

  // Simplified padding: single tab gets extra padding, with compact option
  const paddingClasses = compact
    ? tabs.length === 1
      ? "px-8 py-1"
      : "px-3 py-1"
    : tabs.length === 1
      ? "px-12 py-3"
      : "px-4 py-3";

  return (
    <div className="flex flex-col h-full w-full">
      {/* If `full` is false, center and cap width */}
      <div
        className={[
          "max-w-(100vw)",
          "flex flex-1 min-h-0 flex-col",
          !full && "px-[10vw] max-w-6xl mx-auto",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* Tab Headers */}
        <div className="sticky top-0 z-50 w-full shrink-0 bg-bg-page-body">
          <div
            className={`flex border border-border-subtle rounded-2xl ${
              compact ? "p-[2px]" : "p-1"
            }`}
          >
            {tabs.map((tab, idx) => (
              <div
                key={tab.label}
                className={[
                  "flex-1 flex items-center justify-center relative cursor-pointer rounded-2xl p-1",
                  activeTabIndex === idx && "bg-bg-button-secondary",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleTabSelect(idx)}
              >
                {/* Separator */}
                {idx > 0 && (
                  <div
                    className={`absolute left-0 top-1/2 border-l border-border-subtle -translate-y-1/2 ${
                      compact ? "h-5" : "h-8"
                    }`}
                  />
                )}
                <span
                  className={[
                    "w-full text-center font-medium rounded-2xl transition duration-300 flex items-center justify-center",
                    compact ? "text-[12px]" : "text-[14px]",
                    paddingClasses,
                    activeTabIndex === idx
                      ? "text-white"
                      : "text-text-tab-default",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {tab.label.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Content: min-h-0 so nested grids (e.g. KYB) get a real height and scroll inside */}
        <div className="flex flex-1 min-h-0 w-full flex-col overflow-hidden bg-bg-page-body">
          {tabs[activeTabIndex]?.component}
        </div>
      </div>
    </div>
  );
};

export default TabSwitcherWeb;
